import axios, {AxiosError} from "axios";
import Qs from 'qs';

import {
    OffsetDateTime,
    LocalDate,
    BigDecimal,
    PaginationParams,
    PaginatableResults
} from "./CommonModel"

export enum ConsignmentStatus {
    UNASSIGNED = "UNASSIGNED",
    SCHEDULED = "SCHEDULED",
    ON_ROUTE = "ON_ROUTE",
    AT_DC = "AT_DC",
    ASSIGNED = "ASSIGNED",
    STARTED = "STARTED",
    COMPLETED = "COMPLETED",
    CANCELLED = "CANCELLED"
}

export interface CustomerResponse {
    id: BigInt,
    version: number,
    code: string | null,
    name: string | null
}


export interface AddressResponse {
    id: BigInt,
    unitOrFlat: string | null,
    street: string,
    suburb: string,
    state: string,
    postcode: string | null,
    countryCode: string | null,
    latitude: number | null,
    longitude: number | null
}

export interface ConsignmentWaypointResponse {
    id: BigInt,
    version: number,
    companyName: string | null,
    address: AddressResponse,
    contactName: string | null,
    contactTelephone: string | null,
    from: OffsetDateTime | null,
    to: OffsetDateTime | null,
}

export enum DimensionUnit {
    M = "M",
    CM = "CM",
    MM = "MM"
}

export enum WeightUnit {
    TN = "TN",
    KG = "KG",
    GM = "GM"
}

export enum UnitOfMeasureType {
    QUANTITY_EACH = "QUANTITY_EACH",
    VOLUME_M3 = "VOLUME_M3",
    WEIGHT_TON = "WEIGHT_TON",
    HOURS = "HOURS",
    LOADS = "LOADS",
    PALLET = "PALLET",
    CARTON = "CARTON",
    METRES_SQUARED = "METRES_SQUARED",
    ITEM = "ITEM",
    CONTAINER = "CONTAINER",
    CRATE = "CRATE",
    CASK = "CASK",
    ROLL = "ROLL",
    UNITS = "UNITS",
    LITRES = "LITRES"
}

export interface OrderItem {
    productDescription: string,
    orderedQuantity: BigDecimal
}

export interface ConsignmentFreightResponse extends OrderItem {
    id: BigInt,
    version: number,
    productReference?: string,
    trackingCode?: string,
    height?: BigDecimal,
    width?: BigDecimal,
    length?: BigDecimal,
    dimensionUnit?: DimensionUnit,
    weight?: BigDecimal,
    weightUnit?: WeightUnit,
    unitOfMeasure?: UnitOfMeasureType
}

export interface Consignment {
    version: number,
    status: ConsignmentStatus,
    salesOrderRef: string | null,
    orderDate: LocalDate | null,
    consignmentRef: string,
    customer: CustomerResponse | null,
    pickup: ConsignmentWaypointResponse,
    delivery: ConsignmentWaypointResponse,
    instructions: string | null,
    completedAt: OffsetDateTime | null,
    cancelledAt: OffsetDateTime | null,
    createdAt: OffsetDateTime,
    freight: Array<ConsignmentFreightResponse>
}

export interface ConsignmentSearchParams extends PaginationParams {
    status?: ConsignmentStatus[] | null,
    consignmentRefLike?: string | null,
    salesOrderRefLike?: string | null,
    customerNameLike?: string | null,
    deliveryDate?: LocalDate | null
}


export interface ShopifyOrderItem extends OrderItem {
}

export interface ShopifyLocation {
    name: string,
    addressLine1: string,
    addressLine2: string
}

export interface ShopifyConsignmentPreviewResponse {
    fulfillmentOrderId: BigInt,
    orderId: BigInt,
    orderRef: string,
    orderDate: OffsetDateTime,
    customerName: string | null,
    fulfillmentStatus: string,
    deliveryFrom: OffsetDateTime,
    deliveryTo: OffsetDateTime,
    items: ShopifyOrderItem[],
    pickup: ShopifyLocation,
    delivery: ShopifyLocation
}

export interface ShopifyConsignmentCreateOptions {
    orderId: BigInt,
    fulfillmentOrderId: BigInt,
    deliveryFrom: OffsetDateTime,
    deliveryTo: OffsetDateTime,
    deliveryPhotoRequired: boolean,
    signatureRequired: boolean
}

export interface ShopifyConsignmentCreateRequest {
    fulfillmentOrders: ShopifyConsignmentCreateOptions[]
}


const ConsignmentService = {

    get(
        consignmentId: number,
        resultFunc: (response: Consignment) => void,
        errorFunc: (axiosError: AxiosError) => void,
        finallyFunc: () => void
    ) {
        axios.get(`/shopify/v1/consignments/${consignmentId}`)
            .then(response => resultFunc(response.data))
            .catch(errorFunc)
            .finally(finallyFunc)
    },

    search(
        searchParams: ConsignmentSearchParams,
        resultFunc: (response: PaginatableResults<Consignment>) => void,
        errorFunc: (axiosError: AxiosError) => void,
        finallyFunc: () => void
    ) {
        axios({
            "url": "/shopify/v1/consignments",
            "method": "get",
            paramsSerializer: function(params) {
                return Qs.stringify(params, {arrayFormat: 'repeat'})
            },
            "params": searchParams
        })
            .then(response => resultFunc(new PaginatableResults(response.data)))
            .catch(errorFunc)
            .finally(finallyFunc)
    },

    cancel(
        consignmentRef: string,
        consignmentVersion: number | undefined,
        reason: string,
        resultFunc: () => void,
        errorFunc: (axiosError: AxiosError) => void,
        finallyFunc: () => void
    ) {
        const queryParams = {
            version: consignmentVersion,
            reason
        };
        axios.delete(`/shopify/v1/consignments/${consignmentRef}`, {
            params: queryParams
        })
            .then(response => resultFunc())
            .catch(errorFunc)
            .finally(finallyFunc)
    },

    cancelMultiple(
        consignmentRefs: string[],
        reason: string,
        resultFunc: () => void,
        errorFunc: (axiosError: AxiosError) => void,
        finallyFunc: () => void
    ) {
        const queryParams = {
            ref: consignmentRefs,
            reason
        };
        axios({
            "url": "/shopify/v1/consignments",
            "method": "delete",
            paramsSerializer: function(params) {
                return Qs.stringify(params, {arrayFormat: 'repeat'})
            },
            "params": queryParams
        })
            .then(response => resultFunc())
            .catch(errorFunc)
            .finally(finallyFunc)
    },

    preview(
        ids: String[],
        resultFunc: (response: ShopifyConsignmentPreviewResponse[]) => void,
        errorFunc: (axiosError: AxiosError) => void,
        finallyFunc: () => void
    ) {
        axios({
            "url": "/shopify/v1/consignments/preview",
            "method": "get",
            paramsSerializer: function(params) {
                return Qs.stringify(params, {arrayFormat: 'repeat'})
            },
            "params": {
                id: ids
            }
        })
            .then(response => resultFunc(response.data))
            .catch(errorFunc)
            .finally(finallyFunc)
    },

    create(
        request: ShopifyConsignmentCreateRequest,
        resultFunc: (response: Consignment[]) => void,
        errorFunc: (axiosError: AxiosError) => void,
        finallyFunc: () => void
    ) {
        axios.post("/shopify/v1/consignments", request)
            .then(response => resultFunc(response.data))
            .catch(errorFunc)
            .finally(finallyFunc)
    },
}

export default ConsignmentService;